/* eslint-disable no-use-before-define */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import AdvanceFiltersForm from './AdvanceFiltersForm';
import useTimeline from '../../timeline/useTimeline';
import { stringToArray, removeSpaces } from '../../../../utils/helpers';
import { AssignmentType, VisitStatusWithName } from '../../../../enums';
import { getCancelledTreatmentInstances } from '../../../../actions/areas/planner';
import globals from '../../../../globals/communityGlobals';
import { AdvanceFilters } from './AdvanceFilters';
import {
  VisitTypeFilterHandler,
  VisitStatuseFilterHandler,
  NhsFilterHandler,
  ActivityFilterHandler,
  MultipleCliniciansFilterHandler,
  PostcodeFilterHandler,
  RagFilterHandler,
  BandFilterHandler,
  TreatmentScheduleFilterHandler,
} from './AdvanceFiltersHandler';

const locale = globals.getArea('error');

const getDefaultSelectedValues = () => {
  return AdvanceFilters.reduce((initialValues, filter) => {
    if (filter.name === 'multipleClinicians') {
      initialValues[filter.name] = [1];
    } else {
      initialValues[filter.name] = filter.type === 'select' ? [''] : '';
    }
    return initialValues;
  }, {});
};

const getAdvanceFilterHandlers = () => {
  return {
    visitType: new VisitTypeFilterHandler(),
    visitStatus: new VisitStatuseFilterHandler(),
    nhs: new NhsFilterHandler(),
    activity: new ActivityFilterHandler(),
    multipleClinicians: new MultipleCliniciansFilterHandler(),
    postcode: new PostcodeFilterHandler(),
    rag: new RagFilterHandler(),
    band: new BandFilterHandler(),
    treatmentSchedule: new TreatmentScheduleFilterHandler(),
  };
};

export default function AdvancedFilters({
  selectedDate,
  orgUnitId,
  showAdvanceFiltersState,
  clinicianTreatmentInstances,
  isClinicianTreatmentInstancesDataLoading,
  treatmentInstances,
  dataItems,
  setFilteredInstances,
  hasInstancesChanged,
  setHasInstancesChanged,
  shouldResetAdvanceFilter,
  setShouldResetAdvanceFilter,
  setShowAdvanceFiltersState,
}) {
  const {
    getFlag,
    setAlertMessage,
    getAccessToken,
    shouldResetCancelTreatmentInstance,
    setActiveTreatment,
  } = useTimeline();
  const [isCancelledOptionSelectedState, setIsCancelledOptionSelectedState] = useState(false);
  const [isCancelledTreatmentInstancesDataLoading, setIsCancelledTreatmentInstancesDataLoading] = useState(false);
  const [cancelledTreatmentInstances, setCancelledTreatmentInstances] = useState(null);
  const [advanceFiltersSelectedValue, setAdvanceFiltersSelectedValue] = useState(getDefaultSelectedValues());
  const [advanceFilters, setAdvanceFilters] = useState(AdvanceFilters);
  const prevSelectedDate = useRef();
  const prevOrgUnitId = useRef();

  const isTreatmentInstanceDetailsClosed = () =>
    !getFlag('shouldReloadClinicianTreatments') && !getFlag('showClinicianTreatments');

  const clinicianTreatmentInstancesActivityNames = useMemo(() => {
    const ctreatmentInstances = clinicianTreatmentInstances.flatMap(tis => tis.items);
    return isTreatmentInstanceDetailsClosed() ? [] : ctreatmentInstances.flatMap(ti => stringToArray(ti.activities));
  }, [clinicianTreatmentInstances, isTreatmentInstanceDetailsClosed()]);

  const patientAssignments = useMemo(() => {
    return dataItems.flatMap(dataItem =>
      dataItem.activities.assignments.filter(
        assignment => assignment.assignmentType === AssignmentType.PatientAssignment,
      ),
    );
  }, [dataItems]);

  const activityOptions = useMemo(() => {
    const treatmentInstancesActivityNames = treatmentInstances.flatMap(ti => stringToArray(ti.activities));
    const assignmentActivityNames = patientAssignments.flatMap(pa => stringToArray(pa.primaryLabel));

    const cancelledTreatmentInstancesActivityNames = cancelledTreatmentInstances
      ? cancelledTreatmentInstances.flatMap(cti => cti.treatmentInstanceActivities.map(tia => tia.name))
      : [];

    return [
      ...new Set([
        ...treatmentInstancesActivityNames,
        ...assignmentActivityNames,
        ...clinicianTreatmentInstancesActivityNames,
        ...cancelledTreatmentInstancesActivityNames,
      ]),
    ]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .map(activity => ({ id: activity, name: activity }));
  }, [treatmentInstances, patientAssignments, clinicianTreatmentInstancesActivityNames, cancelledTreatmentInstances]);

  const updateActivityOptions = (options, isDataLoading) => {
    setAdvanceFilters(prevfilters =>
      prevfilters.map(filter =>
        filter.name === 'activity'
          ? { ...filter, options, isLoading: isDataLoading, optionsLength: options.length }
          : filter,
      ),
    );
  };

  useEffect(() => {
    updateActivityOptions(
      activityOptions || [],
      isClinicianTreatmentInstancesDataLoading || isCancelledTreatmentInstancesDataLoading,
    );
  }, [activityOptions, isClinicianTreatmentInstancesDataLoading, isCancelledTreatmentInstancesDataLoading]);

  useEffect(() => {
    const hasSelectedDateChanged = prevSelectedDate.current && prevSelectedDate.current !== selectedDate;
    const hasOrgUnitChanged = prevOrgUnitId.current && prevOrgUnitId.current !== orgUnitId;

    if (hasOrgUnitChanged) {
      handleClear();
      handleCancelledTreatmentInstances(isCancelledOptionSelectedState, hasOrgUnitChanged, hasSelectedDateChanged);
    }

    if (hasSelectedDateChanged && !hasOrgUnitChanged) {
      resetFilteredInstances();
      handleCancelledTreatmentInstances(isCancelledOptionSelectedState, hasOrgUnitChanged, hasSelectedDateChanged);
    }

    if (shouldResetAdvanceFilter) {
      handleClear();
      setShouldResetAdvanceFilter(false);
    }

    if (hasInstancesChanged && !hasSelectedDateChanged && !hasOrgUnitChanged) {
      handleSubmit();
      setHasInstancesChanged(false);
    }

    if (getFlag('shouldResetCancelTreatmentInstance')) {
      handleCancelledTreatmentInstances(isCancelledOptionSelectedState);
    }
    prevOrgUnitId.current = orgUnitId;
    prevSelectedDate.current = selectedDate;
  }, [
    hasInstancesChanged,
    shouldResetAdvanceFilter,
    getFlag('shouldResetCancelTreatmentInstance'),
    selectedDate,
    orgUnitId,
  ]);

  const cancelledTreatmentInstancesCallback = (response, errorMessage) => {
    if (response) {
      setCancelledTreatmentInstances(Object.values(response)[0]);
    }
    if (errorMessage) {
      setAlertMessage(errorMessage);
    }
    setIsCancelledTreatmentInstancesDataLoading(false);
  };

  const handleCancelledTreatmentInstances = (isCancelledOptionSelected, hasOrgUnitChanged, hasSelectedDateChanged) => {
    if (hasOrgUnitChanged) {
      setCancelledTreatmentInstances(null);
      return;
    }
    const forceReload = hasSelectedDateChanged && isCancelledOptionSelected;
    if (
      (isCancelledOptionSelected &&
        (!cancelledTreatmentInstances || getFlag('shouldResetCancelTreatmentInstance')) &&
        !isCancelledTreatmentInstancesDataLoading) ||
      forceReload
    ) {
      setIsCancelledTreatmentInstancesDataLoading(true);
      getCancelledTreatmentInstances(
        getAccessToken,
        { occurrence: selectedDate, orgUnitId },
        false,
        cancelledTreatmentInstancesCallback,
      );
      shouldResetCancelTreatmentInstance(false);
    } else if (
      !isCancelledOptionSelected &&
      (hasSelectedDateChanged || getFlag('shouldResetCancelTreatmentInstance'))
    ) {
      setCancelledTreatmentInstances(null);
      shouldResetCancelTreatmentInstance(false);
    }

    if (isCancelledOptionSelectedState !== isCancelledOptionSelected)
      setIsCancelledOptionSelectedState(isCancelledOptionSelected);
  };

  const resetFilteredInstances = () => {
    setFilteredInstances({
      isAdvanceFilterActive: false,
      shouldFilterAssignments: false,
      filteredTreatmentInstances: [],
      filteredAssignments: [],
      filteredCancelledTreatmentInstances: [],
      filteredClinicianTreatmentInstances: [],
    });
  };

  const clearFilteredInstancesForInvalidInput = () => {
    setFilteredInstances({
      isAdvanceFilterActive: true,
      shouldFilterAssignments: true,
      filteredTreatmentInstances: [],
      filteredAssignments: [],
      filteredCancelledTreatmentInstances: [],
      filteredClinicianTreatmentInstances: clinicianTreatmentInstances
        ? clinicianTreatmentInstances.map(data => ({
            ...data,
            items: [],
          }))
        : [],
    });
  };

  const addFilteredCancelledInstancesInClinicianView = (
    filteredClinicianTreatmentInstances,
    filteredCancelledTreatmentInstances,
  ) => {
    if (clinicianTreatmentInstances && isCancelledOptionSelectedState && filteredCancelledTreatmentInstances) {
      filteredClinicianTreatmentInstances.push({
        name: 'CancelledVisits',
        text: 'Cancelled Visits',
        isAssignable: false,
        items: filteredCancelledTreatmentInstances,
      });
    }
    return filteredClinicianTreatmentInstances;
  };

  const handleAdvanceFilters = (advanceFiltersHandler, shouldFilterAssignments) => {
    resetFilteredInstances();

    const filteredValues = advanceFiltersHandler.handle(
      treatmentInstances,
      shouldFilterAssignments ? patientAssignments : [],
      isCancelledOptionSelectedState ? cancelledTreatmentInstances || [] : [],
      clinicianTreatmentInstances || [],
    );

    setFilteredInstances({
      isAdvanceFilterActive: true,
      shouldFilterAssignments,
      filteredTreatmentInstances: filteredValues.treatmentInstances,
      filteredAssignments: filteredValues.assignments,
      filteredCancelledTreatmentInstances: filteredValues.cancelledTreatmentInstances,
      filteredClinicianTreatmentInstances: addFilteredCancelledInstancesInClinicianView(
        filteredValues.clinicianTreatmentInstances,
        filteredValues.cancelledTreatmentInstances,
      ),
    });
  };

  const shouldFilterAssignments = () => {
    const visitStatusIds = VisitStatusWithName.filter(
      v => v.key === 'unassignedVisits' || v.key === 'cancelledVisits',
    ).map(v => v.id);
    return !visitStatusIds.some(id => advanceFiltersSelectedValue.visitStatus.includes(id));
  };

  const createAdvanceFilterChainHandlers = () => {
    const advanceFilterHandlers = getAdvanceFilterHandlers();
    const advanceFiltersSelectedValueEntites = Object.entries(advanceFiltersSelectedValue).filter(
      ([, request]) => request[0] !== '' && request !== '',
    );

    advanceFiltersSelectedValueEntites.forEach(([handler, request], i) => {
      advanceFilterHandlers[handler].setRequest(request);

      if (i < advanceFiltersSelectedValueEntites.length - 1) {
        const [nextHandler] = advanceFiltersSelectedValueEntites[i + 1];
        advanceFilterHandlers[handler].setNext(advanceFilterHandlers[nextHandler]);
      }
    });

    return advanceFilterHandlers[advanceFiltersSelectedValueEntites[0][0]];
  };

  const getErrorObj = (filterName, message) => {
    return { filterName, message };
  };

  const isNhsTextFieldValid = (value, key, visits) => {
    return value === '' || visits.some(a => removeSpaces(a[key]) === removeSpaces(value));
  };

  const isPostcodeTextFieldValid = (value, key, visits) => {
    return value === '' || visits.some(a => removeSpaces(a[key]).includes(removeSpaces(value)));
  };

  const validateAdvanceFiltersSubmit = (postcode, nhsNumber) => {
    const visits = [
      ...treatmentInstances,
      ...patientAssignments,
      ...(cancelledTreatmentInstances || []),
      ...(clinicianTreatmentInstances.flatMap(tis => tis.items) || []),
    ];
    const errorMessages = [];

    if (!isPostcodeTextFieldValid(postcode.toUpperCase(), 'postcode', visits)) {
      errorMessages.push(getErrorObj('postcode', locale.getMsg('noMatchingRecordsFound')));
    }

    if (!isNhsTextFieldValid(nhsNumber, 'nhsNumber', visits)) {
      errorMessages.push(getErrorObj('nhs', locale.getMsg('noMatchingRecordsFound')));
    }

    return errorMessages;
  };

  const setErrorMessage = (filterName, errorMsg) => {
    setAdvanceFilters(prevfilters =>
      prevfilters.map(filter => (filter.name === filterName ? { ...filter, error: errorMsg } : filter)),
    );
  };

  const resetErrorMessages = () => {
    setErrorMessage('postcode', '');
    setErrorMessage('nhs', '');
  };

  const isValideSubmit = () => {
    resetErrorMessages();
    const errorMessages = validateAdvanceFiltersSubmit(
      advanceFiltersSelectedValue.postcode,
      advanceFiltersSelectedValue.nhs,
    );
    errorMessages.forEach(error => {
      setErrorMessage(error.filterName, error.message);
    });
    return !errorMessages.length;
  };

  const handleSubmit = () => {
    setActiveTreatment(null);
    if (isValideSubmit()) {
      handleAdvanceFilters(createAdvanceFilterChainHandlers(), shouldFilterAssignments());
    } else {
      clearFilteredInstancesForInvalidInput();
    }
  };

  const handleClear = () => {
    resetErrorMessages();
    setAdvanceFiltersSelectedValue(getDefaultSelectedValues());
    setIsCancelledOptionSelectedState(false);
    resetFilteredInstances();
  };

  const handleCancel = () => {
    handleClear();
    setShowAdvanceFiltersState(false);
  };

  return (
    showAdvanceFiltersState && (
      <AdvanceFiltersForm
        handleCancelledTreatmentInstances={handleCancelledTreatmentInstances}
        advanceFiltersSelectedValue={advanceFiltersSelectedValue}
        setAdvanceFiltersSelectedValue={setAdvanceFiltersSelectedValue}
        advanceFilters={advanceFilters}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        handleClear={handleClear}
        isDataLoading={isClinicianTreatmentInstancesDataLoading || isCancelledTreatmentInstancesDataLoading}
      />
    )
  );
}
